import React, { createContext } from 'react'
import { BaseContextState, BaseContextProvider } from '../BaseContext'
import IEnum from '../../interfaces/IEnum'
import IQrCode from '../../interfaces/IQrCode'
import {
  SearchRequest,

  QrCodeCreateRequest,
  QrCodeUpdateRequest,
  QrCodeDeleteRequest
} from '../../services/api/interfaces/ApiRequest'
import {
  qrCodeTypeList,
  qrCodeList,
  qrCodeCreate,
  qrCodeUpdate,
  qrCodeDelete
} from '../../services/api'

interface QrCodeContextState extends BaseContextState {
  qrCodeTypes: Array<IEnum> | null,
  qrCodes: Array<IQrCode> | null,
  qrCode: IQrCode | null
}

export interface QrCodeContextData extends QrCodeContextState {
  resetErrors: Function,

  requestQrCodeTypeList: (args: SearchRequest) => Promise<any>,
  requestQrCodeList: (args: SearchRequest) => Promise<any>,
  requestQrCodeCreate: (args: QrCodeCreateRequest) => Promise<any>,
  requestQrCodeUpdate: (args: QrCodeUpdateRequest) => Promise<any>,
  requestQrCodeDelete: (args: QrCodeDeleteRequest) => Promise<any>,
}

// Create context
const { Provider, Consumer } = createContext({} as QrCodeContextData)

// Export consumer
export const QrCodeConsumer = Consumer

export class QrCodeProvider extends BaseContextProvider {
  state: QrCodeContextState = {
    qrCodeTypes: null,
    qrCodes: null,
    qrCode: null
  }

  /**
   * Make api request for qr code types list
   * 
   * @param {SearchRequest} payload 
   */
   requestQrCodeTypeList = (payload: SearchRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null
      }, async () => {
        const response = await qrCodeTypeList(payload)
  
        // Process our response
        this.processResponse(response, { 
          paginate: {
            destination: 'qrCodeTypes'
          },
        })

        if (this.state.success) {
          resolve(this.state.qrCodes)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  /**
   * Make api request for qr code list
   * 
   * @param {SearchRequest} payload 
   */
  requestQrCodeList = (payload: SearchRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null
      }, async () => {
        const response = await qrCodeList(payload)
  
        // Process our response
        this.processResponse(response, { 
          paginate: {
            destination: 'qrCodes'
          },
        })

        if (this.state.success) {
          resolve(this.state.qrCodes)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  /**
   * Make api request for qr code create
   * 
   * @param {QrCodeCreateRequest} payload 
   */
   requestQrCodeCreate = (payload: QrCodeCreateRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null
      }, async () => {
        const response = await qrCodeCreate(payload)
  
        // Process our response
        this.processResponse(response, { 
          mappings: {
            origin: 'qr_code', destination: 'qrCode'
          }
        })

        if (this.state.success) {
          resolve(this.state.qrCode)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  /**
   * Make api request for qr code update
   * 
   * @param {QrCodeUpdateRequest} payload 
   */
   requestQrCodeUpdate = (payload: QrCodeUpdateRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null
      }, async () => {
        const response = await qrCodeUpdate(payload)
  
        // Process our response
        this.processResponse(response, { 
          mappings: {
            origin: 'qr_code', destination: 'qrCode'
          }
        })

        if (this.state.success) {
          resolve(this.state.qrCode)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  /**
   * Make api request for qr code delete
   * 
   * @param {QrCodeDeleteRequest} payload 
   */
   requestQrCodeDelete = (payload: QrCodeDeleteRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null
      }, async () => {
        const response = await qrCodeDelete(payload)
  
        // Process our response
        this.processResponse(response)

        if (this.state.success) {
          resolve(this.state.qrCode)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  render() {
    return (
      <Provider
        value={{
          // States
          ...this.state,

          // Dispatches
          resetErrors: this.resetErrors,
          requestQrCodeTypeList: this.requestQrCodeTypeList,
          requestQrCodeList: this.requestQrCodeList,
          requestQrCodeCreate: this.requestQrCodeCreate,
          requestQrCodeUpdate: this.requestQrCodeUpdate,
          requestQrCodeDelete: this.requestQrCodeDelete
        }}
      >
        {this.props.children}
      </Provider>
    )
  }
}
